'use client';
import { Card, Spinner } from 'react-bootstrap';
import Badge from 'components/base/Badge';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faHistory,
  faClock,
  faMapPin,
  faPoundSign,
  faCheckCircle,
  faXmarkCircle
} from '@fortawesome/free-solid-svg-icons';
import { useMemo } from 'react';

interface Booking {
  booking_id: number;
  status: string;
  date: string;
  start_time: string;
  end_time: string;
  location: string;
  locum_role: string;
  pay_rate: number;
}

interface BookingHistoryCardProps {
  bookings: Booking[];
  loading: boolean;
}

const BookingHistoryCard = ({ bookings, loading }: BookingHistoryCardProps) => {
  const historyBookings = useMemo(() => {
    return bookings
      .filter(
        booking =>
          booking.status === 'completed' || booking.status === 'cancelled'
      )
      .sort((a, b) => {
        const dateA = new Date(`${a.date} ${a.start_time}`);
        const dateB = new Date(`${b.date} ${b.start_time}`);
        return dateB.getTime() - dateA.getTime();
      })
      .slice(0, 5);
  }, [bookings]);

  const getStatusInfo = (status: string) => {
    switch (status) {
      case 'completed':
        return {
          bg: 'info' as const,
          icon: faCheckCircle,
          label: 'Completed',
          color: 'text-info'
        };
      case 'cancelled':
        return {
          bg: 'danger' as const,
          icon: faXmarkCircle,
          label: 'Cancelled',
          color: 'text-danger'
        };
      default:
        return {
          bg: 'secondary' as const,
          icon: faHistory,
          label: status,
          color: 'text-secondary'
        };
    }
  };

  if (loading) {
    return (
      <Card className="border border-translucent shadow-sm h-100">
        <Card.Header className="bg-body-emphasis">
          <Card.Title className="mb-0 fs-8 text-body-emphasis">
            <FontAwesomeIcon icon={faHistory} className="me-2" />
            Booking History
          </Card.Title>
        </Card.Header>
        <Card.Body className="text-center py-6">
          <Spinner animation="border" role="status" className="text-primary">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className="border border-translucent shadow-sm h-100">
      <Card.Header className="bg-body-emphasis">
        <Card.Title className="mb-0 fs-8 text-body-emphasis d-flex justify-content-between align-items-center">
          <span>
            <FontAwesomeIcon icon={faHistory} className="me-2" />
            Booking History
          </span>
          <Badge variant="phoenix" bg="secondary" className="fs-10">
            {historyBookings.length}
          </Badge>
        </Card.Title>
      </Card.Header>
      <Card.Body className="p-3 p-lg-4">
        {historyBookings.length === 0 ? (
          <div className="text-center py-4">
            <p className="text-body-tertiary mb-0">
              <FontAwesomeIcon icon={faHistory} className="me-2 fs-6" />
              No booking history yet
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {historyBookings.map(booking => {
              const statusInfo = getStatusInfo(booking.status);
              const StatusIcon = statusInfo.icon;

              return (
                <div
                  key={booking.booking_id}
                  className="p-3 rounded-3 border border-translucent transition-all hover-card mb-2"
                >
                  {/* 🔹 Top Row */}
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <div>
                      <h6 className="mb-0 fw-semibold text-body-emphasis">
                        {new Date(booking.date).toLocaleDateString('en-GB', {
                          weekday: 'short',
                          day: '2-digit',
                          month: 'short'
                        })}
                      </h6>
                      <small className="text-body-tertiary">
                        {booking.locum_role}
                      </small>
                    </div>

                    <Badge variant="phoenix" bg={statusInfo.bg}>
                      <FontAwesomeIcon icon={StatusIcon} className="me-1" />
                      {statusInfo.label}
                    </Badge>
                  </div>

                  {/* 🔹 Info Row */}
                  <div className="d-flex justify-content-between flex-wrap gap-2 mb-2">
                    <small className="text-body-tertiary">
                      <FontAwesomeIcon icon={faClock} className="me-1" />
                      {booking.start_time.slice(0, 5)} -{' '}
                      {booking.end_time.slice(0, 5)}
                    </small>

                    <small className="text-body-tertiary text-truncate">
                      <FontAwesomeIcon icon={faMapPin} className="me-1" />
                      {booking.location}
                    </small>
                  </div>

                  {/* 🔹 Bottom Row */}
                  <div className="d-flex justify-content-between align-items-center">
                    <div className={`fw-semibold ${statusInfo.color}`}>
                      <FontAwesomeIcon icon={faPoundSign} className="me-1" />
                      {booking.pay_rate}/hr
                    </div>

                    <small className="text-body-tertiary">
                      {booking.status === 'completed'
                        ? 'Shift Completed'
                        : 'Cancelled'}
                    </small>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default BookingHistoryCard;
